/**
 * @description
 * GitHub settings component for marketplace configuration.
 * Manages GitHub repository settings for app discovery.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';
import { GitHubSettingsProps } from './types';

export function GitHubSettings({ onRefresh }: GitHubSettingsProps) {
  const { config, updateConfig } = useMarketplaceConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        githubRepoOwner: formData.get('githubRepoOwner') as string,
        githubRepoName: formData.get('githubRepoName') as string,
        githubRepoBranch: formData.get('githubRepoBranch') as string,
        githubProductionBranch: formData.get('githubProductionBranch') as string,
        githubStagingBranch: formData.get('githubStagingBranch') as string,
        githubToken: formData.get('githubToken') as string || undefined,
      };

      await updateConfig(data);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update GitHub settings');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    setError(null);

    try {
      const response = await fetch('/api/marketplace/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();
      
      if (result.success) {
        setSyncResult(`Successfully synced ${result.syncedApps || 0} apps from GitHub branches`);
      } else {
        setError(result.message || 'Failed to sync apps from GitHub');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync apps from GitHub');
    } finally {
      setSyncLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">GitHub Configuration</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Configure GitHub repository settings for marketplace app discovery and synchronization.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
        </div>
      )}

      {syncResult && (
        <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4">
          <div className="text-sm text-green-800 dark:text-green-200">{syncResult}</div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="githubRepoOwner" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Repository Owner
            </label>
            <input
              type="text"
              id="githubRepoOwner"
              name="githubRepoOwner"
              defaultValue={config.githubRepoOwner}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="byteable-ai"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              GitHub organization or username that owns the repository
            </p>
          </div>

          <div>
            <label htmlFor="githubRepoName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Repository Name
            </label>
            <input
              type="text"
              id="githubRepoName"
              name="githubRepoName"
              defaultValue={config.githubRepoName}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="byte-platform-cloudflare"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Name of the GitHub repository containing marketplace apps
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="githubRepoBranch" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Default Branch
            </label>
            <input
              type="text"
              id="githubRepoBranch"
              name="githubRepoBranch"
              defaultValue={config.githubRepoBranch}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="production-apps"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Default branch for legacy compatibility
            </p>
          </div>

          <div>
            <label htmlFor="githubProductionBranch" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Production Branch
            </label>
            <input
              type="text"
              id="githubProductionBranch"
              name="githubProductionBranch"
              defaultValue={config.githubProductionBranch || 'production-marketplace'}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="production-marketplace"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Branch containing production-ready marketplace apps
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="githubStagingBranch" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Staging Branch
          </label>
          <input
            type="text"
            id="githubStagingBranch"
            name="githubStagingBranch"
            defaultValue={config.githubStagingBranch || 'staging-marketplace'}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="staging-marketplace"
            required
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Branch containing staging marketplace apps (visible only to admin users)
          </p>
        </div>

        <div>
          <label htmlFor="githubToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            GitHub Personal Access Token
          </label>
          <input
            type="password"
            id="githubToken"
            name="githubToken"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Personal access token with repository access. Leave empty to keep current token.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save GitHub Settings'}
          </button>
        </div>
      </form>

      {/* Manual Sync Section */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Manual Sync</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manually sync apps from GitHub branches to update the marketplace
            </p>
          </div>
          <button
            onClick={handleManualSync}
            disabled={syncLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {syncLoading ? 'Syncing...' : 'Sync Apps from GitHub'}
          </button>
        </div>
      </div>
    </div>
  );
}
